/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import InstanceType from './InstanceType';
import MoserObjectUtils from './MoserObjectUtils';
import ReferenceResolver from './ReferenceResolver';
import ShortcutUtils from './ShortcutUtils';
import UseSpecType from './UseSpecType';
import PropertyType from './PropertyType';
import CommonResolverUtil from './CommonResolverUtil';
import FolderType from './FolderType';
import FolderItemType from './FolderItemType';
import MoserCommonUtils from './MoserCommonUtils';
import DynamicSchemaUtils from './DynamicSchemaUtils';
import EditModeUtils from './EditModeUtils';
import MoserObject from './MoserObject';
import PropertySetter from './PropertySetter';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/generated/metadata/InstanceType (static) // InstanceType
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (runtime) // getList
//@import com/ibm/bi/platform/moser/common/utils/ReferenceResolver (static) // getFirstPart, findRefById
//@import com/ibm/bi/platform/moser/common/utils/ShortcutUtils (static) // isShortcutOrAliasValid
//@import com/ibm/bi/platform/moser/common/generated/metadata/UseSpecType (static) // UseSpecType
//@import com/ibm/bi/platform/moser/common/generated/metadata/PropertyType (static) // new
//@import com/ibm/bi/platform/moser/core/metadata/util/CommonResolverUtil (runtime) // getFMPlaceHolderInMDTV
//@import com/ibm/bi/platform/moser/common/generated/metadata/FolderType (runtime) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/FolderItemType (runtime) // new
//@import com/ibm/bi/platform/moser/common/utils/MoserCommonUtils (runtime) // getObject
//@import com/ibm/bi/platform/moser/common/utils/DynamicSchemaUtils (static) // isOverride, isDataSourceOverrideEmpty
//@import com/ibm/bi/platform/moser/common/utils/EditModeUtils (static) // setEditMode
//@import com/ibm/bi/platform/moser/common/generated/metadata/MoserObject (runtime) // getModuleObject
//@import com/ibm/bi/platform/moser/common/generated/metadata/PropertySetter (runtime) // getParentQuerySubject

/**
 * Util class to convert resolved logical module into a module without any metadata (for saving) This class will be translated into javascript, so think twice before adding new import. All unit tests are in LogicalModuleResolverTest
 * @author  mohamedelgemaiey
 */
export default class DataModuleExtractor extends BaseMoserObject{

	/**
	 * Remove split definition metadata.
	 * @param {QueryItem} parentQI  parent query item.
	 * @param  split
	 */
	static _removeSplitDefinitionMetadata$0(parentQI, splitDefinition) {
		if (!splitDefinition) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = splitDefinition, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var split = __exp_enFor0[__i_enFor0];
			DataModuleExtractor._removeSplitDefinitionMetadata$1(parentQI, split);
		}
	}

	/**
	 * Remove split definition metadata.
	 * @param {QueryItem} parentQI  parent query item.
	 * @param {SplitType} split
	 */
	static _removeSplitDefinitionMetadata$1(parentQI, split) {
		if (InstanceType.REFERENCE === split.getInstanceType()) {
			var items = parentQI.basicGetItem();
			DataModuleExtractor._clearList(items);
		}
		var splitItems = MoserObjectUtils.getList(split.basicGetItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = splitItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var qi = item.getQueryItem();
			if (qi) {
				if (InstanceType.MISSING === qi.getInstanceType()) {
					qi.setInstanceType(InstanceType.REFERENCE);
				} else {
					DataModuleExtractor._removeUnoverridenQueryItemProperties(qi, false);
					DataModuleExtractor._removeGeneratedQueryItemProperties(qi);
					qi.setFacetDefinition(null);
					qi.setSourceCategory(null);
				}
			}
		}
	}

	static _getReferencedModuleUseSpec(qsRef, useSpecList) {
		if (!useSpecList) {
			return null;
		}
		var referencedModuleAlias = ReferenceResolver.getFirstPart(qsRef);
		for (var __i_enFor0 = 0, __exp_enFor0 = useSpecList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var useSpec = __exp_enFor0[__i_enFor0];
			if (useSpec.getIdentifier() == referencedModuleAlias) {
				return useSpec;
			}
		}
		return null;
	}

	static _removeUnoverridenQuerySubjectFilters(qs) {
		var filters = MoserObjectUtils.getList(qs.basicGetFilter());
		for (var i = filters.length - 1; i >= 0; --i) {
			var filter = filters[i];
			var propertyOverride = filter.basicGetPropertyOverride();
			if (!propertyOverride || !(propertyOverride.indexOf("NEW")> -1)) {
				filters.splice(i, 1);
			}
			var propOverrides = filter.basicGetPropertyOverride();
			DataModuleExtractor._clearList(propOverrides);
		}
	}

	static _removeUnoverridenObjectTypeProperties(obj) {
		var propertyOverride = obj.basicGetPropertyOverride();
		if (!propertyOverride || !(propertyOverride.indexOf("label")> -1)) {
			obj.setLabel(null);
		}
		if (!propertyOverride || !(propertyOverride.indexOf("comment")> -1)) {
			obj.setComment(null);
		}
		if (!propertyOverride || !(propertyOverride.indexOf("description")> -1)) {
			obj.setDescription(null);
		}
		if (!propertyOverride || !(propertyOverride.indexOf("screenTip")> -1)) {
			obj.setScreenTip(null);
		}
		if (!propertyOverride || !(propertyOverride.indexOf("hidden")> -1)) {
			obj.setHidden(null);
		}
		DataModuleExtractor._removeUnoverridenProperty(obj.basicGetProperty(), obj.basicGetPropertyOverride());
	}

	static propertyOverrideContains(propertyOverride, propName) {
		return propertyOverride && propertyOverride.indexOf(propName)> -1;
	}

	/**
	 * Remove query item properties that was not overridden
	 * @param {QueryItem} queryItem
	 * @param {boolean} keepPropOverride
	 */
	static _removeUnoverridenQueryItemProperties(queryItem, keepPropOverride) {
		var propertyOverride = queryItem.basicGetPropertyOverride();
		if (propertyOverride && propertyOverride.indexOf("NEW")> -1) {
			DataModuleExtractor._removeSplitDefinitionMetadata$0(queryItem, queryItem.basicGetSplitDefinition());
		} else {
			DataModuleExtractor._removeUnoverridenBaseQueryItemTypeProperties(queryItem);
			DataModuleExtractor._clearList(queryItem.basicGetConcepts());
			queryItem.setMinimumValue(null);
			queryItem.setMaximumValue(null);
			queryItem.setNumberOfDistinctValues(null);
			queryItem.setNumberOfNullValues(null);
			DataModuleExtractor._removeUnoverridenCalculatedMember(queryItem.basicGetCalculatedMember());
		}
		DataModuleExtractor._removeUnoverridenPropertyOverride(queryItem, keepPropOverride);
	}

	/**
	 * Remove Calculation properties that was not overridden.
	 * @param {Calculation} calc  Calculation.
	 * @param {boolean} keepPropOverride
	 */
	static _removeUnoverridenCalculationProperties(calc, keepPropOverride) {
		DataModuleExtractor._removeUnoverridenBaseQueryItemTypeProperties(calc);
		DataModuleExtractor._removeUnoverridenPropertyOverride(calc, keepPropOverride);
	}

	/**
	 * Remove BaseQueryItemType properties that was not overridden.
	 * @param {BaseQueryItemType} baseQueryItemType
	 */
	static _removeUnoverridenBaseQueryItemTypeProperties(baseQueryItemType) {
		var propertyOverride = baseQueryItemType.basicGetPropertyOverride();
		if (!propertyOverride || !(propertyOverride.indexOf("NEW")> -1)) {
			DataModuleExtractor._removeUnoverridenObjectTypeProperties(baseQueryItemType);
			if (!propertyOverride || !(propertyOverride.indexOf("usage")> -1)) {
				baseQueryItemType.setUsage(null);
			}
			if (!propertyOverride || !(propertyOverride.indexOf("regularAggregate")> -1)) {
				baseQueryItemType.setRegularAggregate(null);
			}
			if (!propertyOverride || !(propertyOverride.indexOf("facetDefinition")> -1)) {
				baseQueryItemType.setFacetDefinition(null);
			}
			if (!propertyOverride || !(propertyOverride.indexOf("format")> -1)) {
				baseQueryItemType.setFormat(null);
			}
			if (!(DataModuleExtractor.propertyOverrideContains(propertyOverride, "taxonomy"))) {
				DataModuleExtractor._clearList(baseQueryItemType.basicGetTaxonomy());
			}
			baseQueryItemType.setDatatype(null);
			baseQueryItemType.setNullable(null);
			baseQueryItemType.setExpression(null);
			baseQueryItemType.setIdForExpression(null);
			baseQueryItemType.setHighlevelDatatype(null);
			baseQueryItemType.setDatatypeCategory(null);
			DataModuleExtractor._clearList(baseQueryItemType.basicGetSplitDefinition());
		}
	}

	/**
	 * Remove filter properties that was not overridden.
	 * @param {Filter} flt  Filter.
	 * @param {boolean} keepPropOverride
	 */
	static _removeUnoverridenFilterProperties(flt, keepPropOverride) {
		var propertyOverride = flt.basicGetPropertyOverride();
		if (!propertyOverride || !(propertyOverride.indexOf("NEW")> -1)) {
			DataModuleExtractor._removeUnoverridenObjectTypeProperties(flt);
			if (!propertyOverride || !(propertyOverride.indexOf("postAutoAggregation")> -1)) {
				flt.setPostAutoAggregation(null);
			}
			flt.setExpression(null);
		}
		DataModuleExtractor._removeUnoverridenPropertyOverride(flt, keepPropOverride);
	}

	/**
	 * Remove folder properties that was not overridden.
	 * @param {Folder} folder  Folder.
	 * @param {boolean} keepPropOverride
	 */
	static _removeUnoverridenFolderProperties(folder, keepPropOverride) {
		var propertyOverride = folder.basicGetPropertyOverride();
		if (!propertyOverride || !(propertyOverride.indexOf("NEW")> -1)) {
			DataModuleExtractor._removeUnoverridenObjectTypeProperties(folder);
		}
		DataModuleExtractor._removeUnoverridenPropertyOverride(folder, keepPropOverride);
	}

	/**
	 * Remove namedSet properties that was not overridden.
	 * @param {NamedSet} ns  NamedSet.
	 * @param {boolean} keepPropOverride
	 */
	static _removeUnoverridenNamedSetProperties(ns, keepPropOverride) {
		DataModuleExtractor._removeUnoverridenBaseQueryItemTypeProperties(ns);
		DataModuleExtractor._removeUnoverridenPropertyOverride(ns, keepPropOverride);
	}

	/**
	 * @param {ObjectType} obj
	 * @param {boolean} keepPropOverride
	 */
	static _removeUnoverridenPropertyOverride(obj, keepPropOverride) {
		var propertyOverride = obj.basicGetPropertyOverride();
		if (!propertyOverride) {
			return;
		}
		if (!keepPropOverride) {
			propertyOverride.length = 0;
		} else {
			var ls = [];
			Array.prototype.push.apply(ls, propertyOverride);
			propertyOverride.length = 0;
			for (var __i_enFor0 = 0, __exp_enFor0 = ls, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var s = __exp_enFor0[__i_enFor0];
				if (!(propertyOverride.indexOf(s)> -1)) {
					propertyOverride.push(s);
				}
			}
		}
	}

	static _removeGeneratedCalculationProperties(calc) {
		calc.setIdForExpression(null);
	}

	static _removeGeneratedItemHierarchyProperties(ih) {
		ih.setIdForExpression(null);
	}

	static _removeGeneratedFilterProperties(flt) {
		flt.setIdForExpression(null);
	}

	static _removeGeneratedNamedSetProperties(ns) {
		ns.setIdForExpression(null);
	}

	static _removeGeneratedQueryItemProperties(qi) {
		qi.setIdForExpression(null);
		qi.setHighlevelDatatype(null);
		qi.setDatatypeCategory(null);
	}

	static _removeUnoverridenCalculatedMember(cmList) {
		if (!cmList) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = cmList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var calMember = __exp_enFor0[__i_enFor0];
			var propertyOverride = calMember.basicGetPropertyOverride();
			if (!propertyOverride || !(propertyOverride.indexOf("NEW")> -1)) {
				DataModuleExtractor._removeUnoverridenObjectTypeProperties(calMember);
			}
			DataModuleExtractor._clearList(propertyOverride);
		}
	}

	/**
	 * Remove item hierarchy non overriden properties
	 */
	static _removeUnoverridenItemHierarchyProperties(ih, keepPropOverride) {
		var propertyOverride = ih.basicGetPropertyOverride();
		if (!propertyOverride || !(propertyOverride.indexOf("NEW")> -1)) {
			DataModuleExtractor._removeUnoverridenObjectTypeProperties(ih);
			if (!propertyOverride || !(propertyOverride.indexOf("localRef")> -1)) {
				DataModuleExtractor._clearList(ih.basicGetLocalRef());
			}
			DataModuleExtractor._removeUnoverridenCalculatedMember(ih.basicGetCalculatedMember());
		}
		DataModuleExtractor._removeUnoverridenPropertyOverride(ih, keepPropOverride);
	}

	static _clearList(list) {
		if (list) {
			list.length = 0;
		}
	}

	static emptyList(list) {
		return !list || list.length==0;
	}

	static notEmptyList(list) {
		return list && !(list.length==0);
	}

	static _removeUnoverridenQuerySubjectProperties(qs) {
		DataModuleExtractor._removeUnoverridenObjectTypeProperties(qs);
		DataModuleExtractor._removeUnoverridenQuerySubjectFilters(qs);
		DataModuleExtractor._clearList(qs.basicGetPropertyOverride());
		qs.setNumberOfRows(null);
		qs.setItemNormalization(null);
		qs.setDataCacheExpiry(null);
	}

	static _removeUnoverridenRelationshipProperties(relship) {
		DataModuleExtractor._removeUnoverridenObjectTypeProperties(relship);
		DataModuleExtractor._clearList(relship.basicGetPropertyOverride());
	}

	/**
	 * Remove filter's generated metadata
	 * @param {Array} filters
	 */
	static _removeFilterListGeneratedMetadata(filters) {
		if (!filters) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = filters, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var f = __exp_enFor0[__i_enFor0];
			DataModuleExtractor._removeGeneratedFilterProperties(f);
		}
	}

	static _isImported(obj) {
		var propOverride = obj.basicGetPropertyOverride();
		return propOverride && propOverride.indexOf("Imported")> -1;
	}

	/**
	 * Remove the query subject metadata.
	 * @param {boolean} embed
	 * @param {Module} module
	 * @param {Array} packageModules
	 * @param {boolean} isEmbeddedModule
	 */
	static _removeQuerySubjectMetadata(embed, module, packageModules, isEmbeddedModule) {
		var qsList = MoserObjectUtils.getList(module.basicGetQuerySubject());
		for (var qsIndex = qsList.length - 1; qsIndex >= 0; --qsIndex) {
			var qs = qsList[qsIndex];
			if (InstanceType.REFERENCE === qs.getInstanceType()) {
				DataModuleExtractor._removeReferenceQuerySubjectMetadata(embed, qs, qsList, qsIndex);
			} else if (InstanceType.MISSING === qs.getInstanceType()) {
				DataModuleExtractor._removeMissingQuerySubjectMetadata(module, qs, qsList, qsIndex);
			} else if (isEmbeddedModule || packageModules.indexOf(ReferenceResolver.getFirstPart(qs.getIdentifier()))> -1) {
				qsList.splice(qsIndex, 1);
			} else {
				var items = qs.basicGetItem();
				DataModuleExtractor._removeItemMetadata(items);
				DataModuleExtractor._removeFilterListGeneratedMetadata(qs.basicGetFilter());
				qs.setIdForExpression(null);
				qs.setTreatAs(null);
			}
		}
	}

	/**
	 * @param {boolean} embed
	 * @param {QuerySubject} qs
	 * @param {Array} qsList
	 * @param {int} qsIndex
	 */
	static _removeReferenceQuerySubjectMetadata(embed, qs, qsList, qsIndex) {
		var originalRef = qs.basicGetOriginalRef();
		if (DataModuleExtractor.notEmptyList(originalRef)) {
			var ref = qs.getRef();
			ref.length = 0;
			Array.prototype.push.apply(ref, originalRef);
			originalRef.length = 0;
		}
		var originalOverrideSize = 0;
		var originalOverride = qs.basicGetPropertyOverride();
		if (originalOverride) {
			originalOverrideSize = originalOverride.length;
		}
		var isImported = DataModuleExtractor._isImported(qs);
		if (ShortcutUtils.isShortcutOrAliasValid(qs)) {
			DataModuleExtractor.generatePositionAfter(qs.getItem());
		}
		DataModuleExtractor._removeUnoverridenItems(qs.basicGetItem(), [], embed);
		DataModuleExtractor._removeUnoverridenQuerySubjectProperties(qs);
		DataModuleExtractor._removeFilterListGeneratedMetadata(qs.basicGetFilter());
		if (isImported && originalOverrideSize == 1 && DataModuleExtractor.emptyList(qs.basicGetItem()) && DataModuleExtractor.emptyList(qs.basicGetFilter())) {
			qsList.splice(qsIndex, 1);
		} else {
			qs.setIdForExpression(null);
		}
	}

	/**
	 * @param {Module} module
	 * @param {QuerySubject} qs
	 * @param {Array} qsList
	 * @param {int} qsIndex
	 */
	static _removeMissingQuerySubjectMetadata(module, qs, qsList, qsIndex) {
		if (qs.getTreatAs()) {
			qs.setInstanceType(InstanceType.REFERENCE);
		} else {
			qs.setInstanceType(null);
			var qsRefs = MoserObjectUtils.getList(qs.basicGetRef());
			for (var __i_enFor0 = 0, __exp_enFor0 = qsRefs, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var ref = __exp_enFor0[__i_enFor0];
				var useSpec = DataModuleExtractor._getReferencedModuleUseSpec(ref, module.basicGetUseSpec());
				if (useSpec && UseSpecType.MODULE === useSpec.getType()) {
					qs.setInstanceType(InstanceType.REFERENCE);
					break;
				}
			}
		}
		var propertyOverride = qs.basicGetPropertyOverride();
		if (propertyOverride && propertyOverride.indexOf("-" + "label")> -1) {
			qs.setLabel(null);
		}
		if (DataModuleExtractor._isImported(qs)) {
			qsList.splice(qsIndex, 1);
		} else {
			qs.setIdForExpression(null);
		}
		DataModuleExtractor._clearList(propertyOverride);
	}

	/**
	 * Walks an item list backwards and removes any item that is not overwritten.
	 * @param {Array} items
	 * @param {Array} path
	 * @param {boolean} embed
	 */
	static _removeUnoverridenItems(items, path, embed) {
		if (DataModuleExtractor.emptyList(items)) {
			return;
		}
		for (var i = items.length - 1; i >= 0; --i) {
			path.push("" + i);
			var p = DataModuleExtractor._getPath(path);
			var item = items[i];
			if (item.getQueryItem()) {
				var qi = item.getQueryItem();
				DataModuleExtractor._clearList(qi.basicGetMembers());
				var nestedItems = qi.basicGetItem();
				DataModuleExtractor._removeUnoverridenItems(nestedItems, path, embed);
				if (DataModuleExtractor.emptyList(nestedItems) && DataModuleExtractor.emptyList(qi.basicGetPropertyOverride()) && !(DataModuleExtractor._isCalculatedMemberTypeChanged(qi.basicGetCalculatedMember()))) {
					items.splice(i, 1);
				} else {
					DataModuleExtractor._removeUnoverridenQueryItemProperties(qi, embed);
					DataModuleExtractor._removeGeneratedQueryItemProperties(qi);
					if (embed) {
						DataModuleExtractor._setPathProperty(qi, p, false);
					}
				}
			} else if (item.getNamedSet()) {
				var ns = item.getNamedSet();
				var nestedItems = ns.basicGetItem();
				DataModuleExtractor._removeUnoverridenItems(nestedItems, path, embed);
				if (DataModuleExtractor.emptyList(nestedItems) && DataModuleExtractor.emptyList(ns.basicGetPropertyOverride())) {
					items.splice(i, 1);
				} else {
					DataModuleExtractor._removeUnoverridenNamedSetProperties(ns, embed);
					DataModuleExtractor._removeGeneratedNamedSetProperties(ns);
					if (embed) {
						DataModuleExtractor._setPathProperty(ns, p, false);
					}
				}
			} else if (item.getFilter()) {
				var flt = item.getFilter();
				if (DataModuleExtractor.emptyList(flt.basicGetPropertyOverride())) {
					items.splice(i, 1);
				} else {
					DataModuleExtractor._removeUnoverridenFilterProperties(flt, embed);
					DataModuleExtractor._removeGeneratedFilterProperties(flt);
					if (embed) {
						DataModuleExtractor._setPathProperty(flt, p, false);
					}
				}
			} else if (item.getFolder()) {
				var folder = item.getFolder();
				var nestedItems = folder.basicGetItem();
				DataModuleExtractor._removeUnoverridenItems(nestedItems, path, embed);
				if (DataModuleExtractor.emptyList(nestedItems) && DataModuleExtractor.emptyList(folder.basicGetPropertyOverride())) {
					items.splice(i, 1);
				} else {
					DataModuleExtractor._removeUnoverridenFolderProperties(folder, embed);
					if (embed) {
						DataModuleExtractor._setPathProperty(folder, p, false);
					}
				}
			} else if (item.getItemHierarchy()) {
				var ih = item.getItemHierarchy();
				if (DataModuleExtractor.emptyList(ih.basicGetPropertyOverride()) && !(DataModuleExtractor._isCalculatedMemberTypeChanged(ih.basicGetCalculatedMember()))) {
					items.splice(i, 1);
				} else {
					DataModuleExtractor._removeUnoverridenItemHierarchyProperties(ih, embed);
					DataModuleExtractor._removeGeneratedItemHierarchyProperties(ih);
					if (embed) {
						DataModuleExtractor._setPathProperty(ih, p, false);
					}
				}
			} else if (item.getCalculation()) {
				var calc = item.getCalculation();
				var nestedItems = calc.basicGetItem();
				DataModuleExtractor._removeUnoverridenItems(nestedItems, path, embed);
				if (DataModuleExtractor.emptyList(nestedItems) && DataModuleExtractor.emptyList(calc.basicGetPropertyOverride())) {
					items.splice(i, 1);
				} else {
					DataModuleExtractor._removeUnoverridenCalculationProperties(calc, embed);
					DataModuleExtractor._removeGeneratedCalculationProperties(calc);
					if (embed) {
						DataModuleExtractor._setPathProperty(calc, p, false);
					}
				}
			}
			path.splice(path.length - 1, 1);
		}
	}

	static _getPath(path) {
		var sb = "";
		for (var i = 0; i < path.length; ++i) {
			if (i > 0) {
				sb+=(".");
			}
			sb+=(path[i]);
		}
		return sb;
	}

	static _setPathProperty(obj, p, append) {
		var prop;
		var propList = obj.getProperty();
		for (var __i_enFor0 = 0, __exp_enFor0 = propList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var pp = __exp_enFor0[__i_enFor0];
			if ("_path" == pp.getName()) {
				prop = pp;
				break;
			}
		}
		if (!prop) {
			prop = new PropertyType();
			prop.setName("_path");
			propList.push(prop);
		}
		if (!append) {
			prop.setValue(p);
		} else {
			var old = prop.getValue();
			if (old == null) {
				prop.setValue(p);
			} else {
				prop.setValue(old + ";" + p);
			}
		}
	}

	/**
	 * @param {Array} items
	 */
	static _removeItemMetadata(items) {
		if (!items) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var qi = item.getQueryItem();
			if (qi) {
				DataModuleExtractor._removeGeneratedQueryItemProperties(qi);
				DataModuleExtractor._removeSplitDefinitionMetadata$0(qi, qi.basicGetSplitDefinition());
				DataModuleExtractor._removeMembers(qi);
				if (qi.basicGetItem()) {
					DataModuleExtractor._removeItemMetadata(qi.basicGetItem());
				}
			}
			var flt = item.getFilter();
			if (flt) {
				flt.setIdForExpression(null);
				DataModuleExtractor._clearList(flt.basicGetPropertyOverride());
			}
			var folder = item.getFolder();
			if (folder) {
				DataModuleExtractor._removeItemMetadata(folder.basicGetItem());
			}
		}
	}

	/**
	 * Clears the list of members from all levels in a QueryItem
	 * @param {QueryItem} qi  - a QueryItem object to have members removed from
	 */
	static _removeMembers(qi) {
		DataModuleExtractor._clearList(qi.basicGetMembers());
		var items = MoserObjectUtils.getList(qi.basicGetItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var qi2 = item.getQueryItem();
			if (qi2) {
				DataModuleExtractor._removeMembers(qi2);
			}
		}
	}

	static _isCalculatedMemberTypeChanged(calcMembList) {
		if (DataModuleExtractor.notEmptyList(calcMembList)) {
			for (var __i_enFor0 = 0, __exp_enFor0 = calcMembList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var calcMember = __exp_enFor0[__i_enFor0];
				if (DataModuleExtractor.notEmptyList(calcMember.basicGetPropertyOverride())) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * remove calculation metadata
	 * @param {Array} calcList
	 * @param {Array} packageModules
	 */
	static _removeCalculationMetadata(calcList, packageModules) {
		if (!calcList) {
			return;
		}
		for (var i = calcList.length - 1; i >= 0; --i) {
			var calc = calcList[i];
			if (DataModuleExtractor._isImported(calc)) {
				var propertyOverride = calc.basicGetPropertyOverride();
				if (propertyOverride.length == 1) {
					calcList.splice(i, 1);
				} else {
					DataModuleExtractor._removeUnoverridenCalculationProperties(calc, false);
				}
			} else if (packageModules.indexOf(ReferenceResolver.getFirstPart(calc.getIdentifier()))> -1) {
				calcList.splice(i, 1);
			} else {
				DataModuleExtractor._removeGeneratedCalculationProperties(calc);
			}
		}
	}

	/**
	 * remove relationship metadata
	 * @param {Array} relList
	 */
	static _removeRelationshipMetadata(relList) {
		if (!relList) {
			return;
		}
		for (var i = relList.length - 1; i >= 0; --i) {
			var relship = relList[i];
			if (relship.getTransientSource()) {
				relList.splice(i, 1);
				continue;
			}
			if (InstanceType.REFERENCE === relship.getInstanceType() || InstanceType.MISSING === relship.getInstanceType()) {
				if (DataModuleExtractor._isImported(relship) && DataModuleExtractor.notEmptyList(relship.basicGetPropertyOverride()) && relship.getPropertyOverride().length == 1) {
					relList.splice(i, 1);
				} else {
					DataModuleExtractor._removeUnoverridenRelationshipProperties(relship);
					relship.setLeft(null);
					relship.setRight(null);
					DataModuleExtractor._clearList(relship.basicGetLink());
					if (InstanceType.MISSING === relship.getInstanceType()) {
						relship.setInstanceType(InstanceType.REFERENCE);
					}
				}
			} else if (DataModuleExtractor._isImported(relship)) {
				relList.splice(i, 1);
			}
		}
	}

	/**
	 * Remove module filters' metadata
	 * @param {Array} filterList
	 * @param {Array} packageModules
	 */
	static _removeGlobalFilterMetadata(filterList, packageModules) {
		if (!filterList) {
			return;
		}
		for (var i = filterList.length - 1; i >= 0; --i) {
			var filter = filterList[i];
			if (DataModuleExtractor._isImported(filter) || packageModules.indexOf(ReferenceResolver.getFirstPart(filter.getIdentifier()))> -1) {
				filterList.splice(i, 1);
			}
		}
	}

	/**
	 * Remove module drill group metadata
	 * @param {Array} basicDrillGroupList
	 * @param {Array} packageModules
	 */
	static _removeDrillGroupMetadata(basicDrillGroupList, packageModules) {
		DataModuleExtractor._removeListMetadata(basicDrillGroupList, packageModules);
		var drillGroupList = MoserObjectUtils.getList(basicDrillGroupList);
		for (var __i_enFor0 = 0, __exp_enFor0 = drillGroupList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var drillGroup = __exp_enFor0[__i_enFor0];
			drillGroup.setIdForExpression(null);
			var segmentList = MoserObjectUtils.getList(drillGroup.basicGetSegment());
			for (var __i_enFor1 = 0, __exp_enFor1 = segmentList, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var segment = __exp_enFor1[__i_enFor1];
				segment.setIdForExpression(null);
			}
		}
	}

	/**
	 * Remove imported metadata
	 * @param {Array} list
	 */
	static _removeListMetadata(list, packageModules) {
		if (!list) {
			return;
		}
		for (var i = list.length - 1; i >= 0; --i) {
			var obj = list[i];
			if (DataModuleExtractor._isImported(obj) || packageModules.indexOf(ReferenceResolver.getFirstPart(obj.getIdentifier()))> -1) {
				list.splice(i, 1);
			}
		}
	}

	/**
	 * Remove package's MDTV from the module
	 * @param {Module} module
	 * @param {Array} packageModules
	 */
	static _removeMDTV(module, packageModules) {
		if (!packageModules) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = packageModules, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var moduleAlias = __exp_enFor0[__i_enFor0];
			var placeHolder = CommonResolverUtil.getFMPlaceHolderInMDTV(module, moduleAlias);
			if (!placeHolder) {
				continue;
			}
			placeHolder.setLabel(null);
			placeHolder.setTransientSource(null);
			DataModuleExtractor._clearList(placeHolder.basicGetFolderItem());
			var propertyOverride = placeHolder.basicGetPropertyOverride();
			if (propertyOverride && DataModuleExtractor._isImported(placeHolder) && propertyOverride.indexOf("Imported")> -1) {
				for (var i = propertyOverride.length - 1; i >= 0; --i) {
					var prop = propertyOverride[i];
					if ("Imported" == prop) {
						propertyOverride.splice(i, 1);
					}
				}
			}
			if (placeHolder.getIdentifier() == null && propertyOverride && propertyOverride.indexOf(moduleAlias)> -1) {
				placeHolder.setIdentifier(moduleAlias);
				propertyOverride.length = 0;
			}
		}
	}

	static _removeImportedMDTV(module, packageModules) {
		var mdtv = module.basicGetMetadataTreeView();
		if (DataModuleExtractor.notEmptyList(mdtv)) {
			var dummyFolderForNewFolderItems = new FolderType();
			var fiList = MoserObjectUtils.getList(mdtv[0].basicGetFolderItem());
			for (var i = fiList.length - 1; i >= 0; --i) {
				var fi = fiList[i];
				var folder = fi.getFolder();
				if (folder && DataModuleExtractor._isImported(folder)) {
					fiList.splice(i, 1);
					var path = [];
					path.push("" + i);
					DataModuleExtractor._collectNewFolderItems(dummyFolderForNewFolderItems, folder, path, module);
					continue;
				}
				var ref = fi.getRef();
				if (ref != null) {
					var obj = ReferenceResolver.findRefById(ref, module);
					if (!obj || DataModuleExtractor._isImported(obj)) {
						fiList.splice(i, 1);
					}
				} else {
					var f = fi.getFolder();
					var id = f.getIdentifier();
					if (id != null && id.indexOf("M") == 0 && packageModules.indexOf(id)> -1) {
						fiList.splice(i, 1);
					}
				}
			}
			if (fiList.length==0 && !dummyFolderForNewFolderItems.basicGetFolderItem()) {
				module.getMetadataTreeView().splice(0, 1);
			}
			if (dummyFolderForNewFolderItems.basicGetFolderItem()) {
				var dummyFolderItem = new FolderItemType();
				dummyFolderItem.setFolder(dummyFolderForNewFolderItems);
				fiList.push(dummyFolderItem);
			}
		}
	}

	/**
	 * Assigns a position after value for a list of items.
	 * @param {Array} items 	list of item types.
	 */
	static generatePositionAfter(items) {
		var previous;
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			if (!previous) {
				item.setPositionAfter(null);
			} else {
				var object = MoserCommonUtils.getObject(previous);
				if (object) {
					item.setPositionAfter(object.getIdentifier());
				} else {
					item.setPositionAfter(null);
				}
			}
			previous = item;
		}
	}

	/**
	 * collect Folder Items for the newly created objects like calculations from existing folder of data module. preserve them with position in dummyFolderForNewFolderItems.
	 * @param {FolderType} dummyFolderForNewFolderItems 	dummy folder containing Folder Items for the newly created objects from existing folder of data module.
	 * @param {FolderType} folder  a folder in MDTV.
	 * @param {Array} path  the chain position, e.g. 0.1
	 * @param {Module} module  the current embed module.
	 */
	static _collectNewFolderItems(dummyFolderForNewFolderItems, folder, path, module) {
		var fiList = folder.basicGetFolderItem();
		if (!fiList) {
			return;
		}
		for (var i = 0; i < fiList.length; ++i) {
			path.push("" + i);
			var fi = fiList[i];
			var subFolder = fi.getFolder();
			if (subFolder && DataModuleExtractor._isImported(subFolder)) {
				DataModuleExtractor._collectNewFolderItems(dummyFolderForNewFolderItems, subFolder, path, module);
			} else {
				var ref = fi.getRef();
				if (ref != null) {
					var obj = ReferenceResolver.findRefById(ref, module);
					if (obj && !(DataModuleExtractor._isImported(obj))) {
						dummyFolderForNewFolderItems.getFolderItem().push(fi);
						var idOfParentFolder = folder.getIdentifier();
						var p = null;
						if (idOfParentFolder != null && !(idOfParentFolder.length === 0)) {
							p = idOfParentFolder + "." + i;
						} else {
							p = DataModuleExtractor._getPath(path);
						}
						DataModuleExtractor._setPathProperty(dummyFolderForNewFolderItems, p, true);
					}
				}
			}
			path.splice(path.length - 1, 1);
		}
	}

	static _isResolvedUseSpec(useSpec) {
		return 0 == useSpec.getIdentifier().indexOf("__Base");
	}

	/**
	 * Remove all useSpec if is starts with "__Base"
	 * @param {Array} useSpecList
	 */
	static _removeResolvedUseSpec(module, useSpecList, isEmbeddedModule) {
		if (!useSpecList) {
			return;
		}
		for (var i = useSpecList.length - 1; i >= 0; --i) {
			var useSpec = useSpecList[i];
			if (DataModuleExtractor._isResolvedUseSpec(useSpec) || isEmbeddedModule && !("*" == useSpec.getImports()) && !(DataModuleExtractor._isReferencedUseSpec(useSpec, module))) {
				useSpecList.splice(i, 1);
			}
		}
	}

	static _removeDataSourceOverride(useSpecList) {
		if (!useSpecList) {
			return;
		}
		for (var i = useSpecList.length - 1; i >= 0; --i) {
			var useSpec = useSpecList[i];
			if (UseSpecType.DATABASE == useSpec.getType()) {
				var dataSourceOverride = useSpec.getDataSourceOverride();
				if (dataSourceOverride) {
					var origDataSource = dataSourceOverride.getOriginalCMDataSource();
					var origCatalog = dataSourceOverride.getOriginalCatalog();
					var origSchema = dataSourceOverride.getOriginalSchema();
					var dataSource = dataSourceOverride.getCmDataSource();
					var catalog = dataSourceOverride.getCatalog();
					var schema = dataSourceOverride.getSchema();
					var isOverrideCMDataSource = DynamicSchemaUtils.isOverride(dataSourceOverride.isOverrideCMDataSource());
					var isOverrideCatalog = DynamicSchemaUtils.isOverride(dataSourceOverride.isOverrideCatalog());
					var isOverrideSchema = DynamicSchemaUtils.isOverride(dataSourceOverride.isOverrideSchema());
					if (!isOverrideCMDataSource && origDataSource == dataSource) {
						dataSourceOverride.setCmDataSource(null);
					}
					if (!isOverrideCatalog && origCatalog == catalog) {
						dataSourceOverride.setCatalog(null);
					}
					if (!isOverrideSchema && origSchema == schema) {
						dataSourceOverride.setSchema(null);
					}
					dataSourceOverride.setOriginalCMDataSource(null);
					dataSourceOverride.setOriginalCatalog(null);
					dataSourceOverride.setOriginalSchema(null);
					if (DynamicSchemaUtils.isDataSourceOverrideEmpty(dataSourceOverride)) {
						useSpec.setDataSourceOverride(null);
					}
				}
			} else if (useSpec.getDataSourceOverride()) {
				useSpec.setDataSourceOverride(null);
			}
		}
	}

	/**
	 * Check if a UseSpec is referenced in the Module definition
	 * @param {UseSpec} useSpec
	 * @param {Module} module
	 * @return {boolean}
	 */
	static _isReferencedUseSpec(useSpec, module) {
		var querySubjects = module.basicGetQuerySubject();
		if (querySubjects && !(querySubjects.length==0)) {
			for (var __i_enFor0 = 0, __exp_enFor0 = querySubjects, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qs = __exp_enFor0[__i_enFor0];
				var u = DataModuleExtractor.getUseSpec(qs, module, true);
				if (useSpec == u) {
					return true;
				}
			}
		}
		var relationships = module.basicGetRelationship();
		if (relationships && !(relationships.length==0)) {
			for (var __i_enFor1 = 0, __exp_enFor1 = relationships, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var rel = __exp_enFor1[__i_enFor1];
				var u = DataModuleExtractor.getUseSpec(rel, module, true);
				if (useSpec == u) {
					return true;
				}
			}
		}
		var calculations = module.basicGetCalculation();
		if (calculations && !(calculations.length==0)) {
			for (var __i_enFor2 = 0, __exp_enFor2 = calculations, __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var calc = __exp_enFor2[__i_enFor2];
				var u = DataModuleExtractor.getUseSpec(calc, module, true);
				if (useSpec == u) {
					return true;
				}
			}
		}
		var filters = module.basicGetFilter();
		if (filters && !(filters.length==0)) {
			for (var __i_enFor3 = 0, __exp_enFor3 = filters, __len_enFor3 = __exp_enFor3.length;
					__i_enFor3 < __len_enFor3; ++__i_enFor3) {
				var filter = __exp_enFor3[__i_enFor3];
				var u = DataModuleExtractor.getUseSpec(filter, module, true);
				if (useSpec == u) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * @param {UseSpec} useSpec
	 * @param {Module} module
	 * @return {Array}
	 */
	static getUseSpecRefs(useSpec, module) {
		var result = [];
		var querySubjects = module.basicGetQuerySubject();
		if (querySubjects && !(querySubjects.length==0)) {
			for (var __i_enFor0 = 0, __exp_enFor0 = querySubjects, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qs = __exp_enFor0[__i_enFor0];
				var u = DataModuleExtractor.getUseSpec(qs, module, true);
				if (useSpec == u) {
					result.push(qs);
				}
			}
		}
		var relationships = module.basicGetRelationship();
		if (relationships && !(relationships.length==0)) {
			for (var __i_enFor1 = 0, __exp_enFor1 = relationships, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var rel = __exp_enFor1[__i_enFor1];
				var u = DataModuleExtractor.getUseSpec(rel, module, true);
				if (useSpec == u) {
					result.push(rel);
				}
			}
		}
		var calculations = module.basicGetCalculation();
		if (calculations && !(calculations.length==0)) {
			for (var __i_enFor2 = 0, __exp_enFor2 = calculations, __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var calc = __exp_enFor2[__i_enFor2];
				var u = DataModuleExtractor.getUseSpec(calc, module, true);
				if (useSpec == u) {
					result.push(calc);
				}
			}
		}
		var filters = module.basicGetFilter();
		if (filters && !(filters.length==0)) {
			for (var __i_enFor3 = 0, __exp_enFor3 = filters, __len_enFor3 = __exp_enFor3.length;
					__i_enFor3 < __len_enFor3; ++__i_enFor3) {
				var filter = __exp_enFor3[__i_enFor3];
				var u = DataModuleExtractor.getUseSpec(filter, module, true);
				if (useSpec == u) {
					result.push(filter);
				}
			}
		}
		return result;
	}

	/**
	 * If you have a ObjectType, it may have a ref that point to an object in a source. This method returns the UseSpec that point to that source.
	 * @param {ObjectType} obj  : ObjectType to find UseSpec for.
	 * @param {Module} m  : the module that contains the object. if null, we'll try to find it using getParent()
	 * @param {boolean} originalRef  : whether to check the original ref first
	 * @return {UseSpec}  UseSpec of the referenced source or null if can't find it.
	 */
	static getUseSpec(obj, m, originalRef) {
		if (!obj) {
			return null;
		}
		if ("QuerySubject" == obj.getObjectType()) {
			return DataModuleExtractor._getQuerySubjectUseSpec(obj, m, originalRef);
		}
		if ("Relationship" == obj.getObjectType()) {
			return DataModuleExtractor.getUseSpecByRef(m, (obj).getRef(), true);
		}
		if ("Filter" == obj.getObjectType()) {
			return DataModuleExtractor.getUseSpecByRef(m, obj.getIdentifier(), true);
		}
		if ("Calculation" == obj.getObjectType()) {
			var spec = DataModuleExtractor.getUseSpecByRef(m, obj.getIdentifier(), true);
			if (!spec) {
				var pos = DataModuleExtractor._getImportAllUseSpecPosition(m.basicGetUseSpec());
				if (pos >= 0 && obj.isImported()) {
					return m.getUseSpec()[pos];
				}
			}
			return spec;
		}
		if ("FolderType" == obj.getObjectType()) {
			if (obj.getIdentifier() != null) {
				return DataModuleExtractor.getUseSpecById(m, obj.getIdentifier());
			}
			var parentFolderType = DataModuleExtractor.getParentFolderTypeWithIdDefined(obj);
			if (parentFolderType) {
				return DataModuleExtractor.getUseSpecById(m, parentFolderType.getIdentifier());
			}
		}
		return null;
	}

	/**
	 * @param {MoserObject} obj  : MoserObject
	 * @return {FolderType}  the first parent folder type with an identifier defined.
	 */
	static getParentFolderTypeWithIdDefined(obj) {
		if (!obj) {
			return null;
		}
		var parent = obj.getParent();
		if (!parent) {
			return null;
		}
		if ("FolderItemType" == parent.getObjectType()) {
			return DataModuleExtractor.getParentFolderTypeWithIdDefined(parent);
		} else if ("FolderType" == parent.getObjectType()) {
			var parentFolderType = parent;
			if (parentFolderType.getIdentifier() != null) {
				return parentFolderType;
			}
			return DataModuleExtractor.getParentFolderTypeWithIdDefined(parent);
		}
		return null;
	}

	static _getUseSpecByRefs(module, refs, canBePackage) {
		if (refs && !(refs.length==0)) {
			return DataModuleExtractor.getUseSpecByRef(module, refs[0], canBePackage);
		}
		return null;
	}

	/**
	 * If you have a QuerySubject, it may have a ref that point to an object in a source. This method returns the UseSpec that point to that source.
	 * @param {QuerySubject} qs  : QuerySubject to find UseSpec for.
	 * @param  m  : the module that contains the object. if null, we'll try to find it using getParent()
	 * @param {boolean} originalRef  : whether to check the original ref first
	 * @return {UseSpec}  UseSpec of the referenced source or null if can't find it.
	 */
	static _getQuerySubjectUseSpec(qs, module, originalRef) {
		var useSpec;
		if (originalRef) {
			useSpec = DataModuleExtractor._getUseSpecByRefs(module, qs.basicGetOriginalRef(), true);
			if (useSpec) {
				return useSpec;
			}
		}
		useSpec = DataModuleExtractor._getUseSpecByRefs(module, qs.basicGetRef(), false);
		if (useSpec) {
			return useSpec;
		}
		if (!originalRef) {
			useSpec = DataModuleExtractor._getUseSpecByRefs(module, qs.basicGetOriginalRef(), true);
			if (useSpec) {
				return useSpec;
			}
		}
		return DataModuleExtractor.getUseSpecByRef(module, qs.getIdentifier(), true);
	}

	static getUseSpecByRef(module, ref, canBePackage) {
		var moduleAlias = ReferenceResolver.getFirstPart(ref);
		if (moduleAlias != null && !(moduleAlias.length === 0)) {
			var useSpec = DataModuleExtractor.getUseSpecById(module, moduleAlias);
			if (!canBePackage && useSpec && UseSpecType.PACKAGE === useSpec.getType()) {
				return null;
			}
			return useSpec;
		}
		return null;
	}

	/**
	 * get a usespec in a module using the useSpecId
	 * @param {Module} module
	 * @param {String} useSpecId
	 * @return {UseSpec}
	 */
	static getUseSpecById(module, useSpecId) {
		var uSpecs = MoserObjectUtils.getList(module.basicGetUseSpec());
		for (var __i_enFor0 = 0, __exp_enFor0 = uSpecs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var useSpec = __exp_enFor0[__i_enFor0];
			if (useSpecId == useSpec.getIdentifier()) {
				return useSpec;
			}
		}
		return null;
	}

	/**
	 * Remove all PropertyType from propertyList that are not included in the propertyOverride.
	 * @param {Array} propertyList
	 * @param {Array} propertyOverride
	 */
	static _removeUnoverridenProperty(propertyList, propertyOverride) {
		if (!propertyList) {
			return;
		}
		for (var i = propertyList.length - 1; i >= 0; --i) {
			var property = propertyList[i];
			if (!propertyOverride || !(propertyOverride.indexOf("property." + property.getName())> -1)) {
				propertyList.splice(i, 1);
			}
		}
	}

	/**
	 * Remove all metadata information from the module. i.e. unresolve the logical module
	 * @param {Module} module
	 */
	static removeModuleMetadata(module) {
		var overrideMode = module.isOverrideMode();
		module.setOverrideMode(false);
		DataModuleExtractor._clearList(module.basicGetUse());
		DataModuleExtractor._clearList(module.basicGetDataSource());
		var useSpecList = MoserObjectUtils.getList(module.basicGetUseSpec());
		var packageModules = [];
		var isEmbeddedModule = DataModuleExtractor.isEmbeddedModule(useSpecList);
		for (var __i_enFor0 = 0, __exp_enFor0 = useSpecList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var useSpec = __exp_enFor0[__i_enFor0];
			if (UseSpecType.PACKAGE === useSpec.getType() || UseSpecType.LW_OLAP === useSpec.getType() || UseSpecType.REPORT === useSpec.getType()) {
				packageModules.push(useSpec.getIdentifier());
			}
		}
		DataModuleExtractor._removeMDTV(module, packageModules);
		if (isEmbeddedModule) {
			DataModuleExtractor._removeImportedMDTV(module, packageModules);
		}
		DataModuleExtractor._removeQuerySubjectMetadata(isEmbeddedModule, module, packageModules, DataModuleExtractor.isEmbeddedModuleForPackage(module.basicGetUseSpec()));
		DataModuleExtractor._removeCalculationMetadata(module.basicGetCalculation(), packageModules);
		DataModuleExtractor._removeGlobalFilterMetadata(module.basicGetFilter(), packageModules);
		DataModuleExtractor._removeRelationshipMetadata(module.basicGetRelationship());
		DataModuleExtractor._removeDrillGroupMetadata(module.basicGetDrillGroup(), packageModules);
		DataModuleExtractor._removeListMetadata(module.basicGetParameterMap(), packageModules);
		DataModuleExtractor._removeResolvedUseSpec(module, useSpecList, isEmbeddedModule);
		DataModuleExtractor._removeDataSourceOverride(useSpecList);
		DataModuleExtractor._removeNamedSetMetadata(module);
		DataModuleExtractor._removeTranslationMetadata(module);
		DataModuleExtractor._removeCustomSortMetadata(module);
		if (isEmbeddedModule) {
			var propertyOverride = module.basicGetPropertyOverride();
			DataModuleExtractor._removeUnoverridenProperty(module.basicGetProperty(), propertyOverride);
		}
		EditModeUtils.setEditMode(module, null, false);
		module.setOverrideMode(overrideMode);
	}

	/**
	 * Removes all members from all QueryItems within a given QuerySubject. This method extracts QueryItems from the items list.
	 * @param {QuerySubject} qs  - a QuerySubject object to have members removed from
	 */
	static removeMembersFromQS(qs) {
		var items = qs.basicGetItem();
		if (items) {
			for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var item = __exp_enFor0[__i_enFor0];
				var qi = item.getQueryItem();
				if (qi) {
					DataModuleExtractor._removeMembers(qi);
				}
			}
		}
	}

	static _removeNamedSetMetadata(module) {
		DataModuleExtractor._clearList(module.basicGetNamedSet());
	}

	/**
	 * Traverses translation list in module and removes metadata from translation objects.
	 * @param {Module} module
	 */
	static _removeTranslationMetadata(module) {
		var translationList = MoserObjectUtils.getList(module.basicGetTranslation());
		for (var __i_enFor0 = 0, __exp_enFor0 = translationList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var translation = __exp_enFor0[__i_enFor0];
			translation.setIdForExpression(null);
		}
	}

	/**
	 * if any one imports * then it's embedded
	 * @param {Array} useSpecs
	 * @return {boolean}  true if any one imports * then it's embedded
	 */
	static isEmbeddedModule(useSpecs) {
		return DataModuleExtractor._getImportAllUseSpecPosition(useSpecs) >= 0;
	}

	/**
	 * get the position of the IMPORT_ALL UseSpec in useSpecs.
	 * @param {Array} useSpecs
	 * @return {int}  position or -1 if there is no IMPORT_ALL UseSpec.
	 */
	static _getImportAllUseSpecPosition(useSpecs) {
		if (!(DataModuleExtractor.notEmptyList(useSpecs))) {
			return -1;
		}
		for (var i = 0; i < useSpecs.length; ++i) {
			if ("*" == useSpecs[i].getImports()) {
				return i;
			}
		}
		return -1;
	}

	/**
	 * This is a very narrow definition of an embedded module based on package used by dashboard. There is always only one package in the use spec, and it is always exactly one use, to a package
	 * @param  module - Input module.
	 * @return {boolean}
	 */
	static isEmbeddedModuleForPackage(useSpecs) {
		var pos = DataModuleExtractor._getImportAllUseSpecPosition(useSpecs);
		return pos >= 0 && (UseSpecType.PACKAGE === useSpecs[pos].getType() || UseSpecType.REPORT === useSpecs[pos].getType());
	}

	/**
	 * Returns the UseSpec for given a object type.
	 * @param {ObjectType} obj   ObjectType
	 */
	static getUseSpecByObjectType(obj) {
		if (!obj) {
			return null;
		}
		var module = MoserObject.getModuleObject(obj);
		if (!module) {
			return null;
		}
		var parentQS = PropertySetter.getParentQuerySubject(obj);
		var useSpec;
		if (parentQS) {
			useSpec = DataModuleExtractor.getUseSpec(parentQS, module, true);
		} else {
			useSpec = DataModuleExtractor.getUseSpec(obj, module, true);
		}
		return useSpec;
	}

	/**
	 * @param {Module} module
	 */
	static _removeCustomSortMetadata(module) {
		var customSorts = module.basicGetCustomSort();
		if (customSorts) {
			for (var i = customSorts.length - 1; i >= 0; --i) {
				var cs = customSorts[i];
				if (DataModuleExtractor._isImported(cs)) {
					customSorts.splice(i, 1);
				} else {
					cs.setIdForExpression(null);
				}
			}
		}
	}

	static removeSplitDefinitionMetadata(a0, a1) {
		var args = arguments;
		if (args.length == 2 && (a1 == null || Object.prototype.toString.call(a1) === "[object Array]")) {
			return DataModuleExtractor.removeSplitDefinitionMetadata$0(a0, a1);
		}
		return DataModuleExtractor.removeSplitDefinitionMetadata$1(a0, a1);
	}

	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/DataModuleExtractor.js